// A 504 is not retried, since it already spent the server's route budget.
// networkMode is "always" because OfflineOverlay owns offline. `await
// mutateAsync()` settles after the keys the mutation waits for have refetched:
// its `awaits`, or everything it `invalidates` when it names none. The portal's
// status refetches behind it, since it is the heaviest read either portal makes,
// unless the mutation says `status: "await"`. However long the reads take, the
// wait ends after MUTATION_REFETCH_WAIT_MS: the write has already committed.

import {
  MutationCache,
  partialMatchKey,
  QueryClient,
  skipToken,
  type DefaultOptions,
  type QueryKey,
} from "@tanstack/vue-query";

import { isApiError } from "@/services/apiError";

const MAX_RETRIES = 2;
const MAX_RETRY_DELAY_MS = 8_000;

export const DEFAULT_STALE_TIME_MS = 30_000;
export const DEFAULT_GC_TIME_MS = 5 * 60_000;

// A save holds its button while the reads it waits for refetch, so the screen
// after it shows the write. The write has committed by then, so a read that
// hangs must not hold the button: past this the reads finish in the background.
export const MUTATION_REFETCH_WAIT_MS = 2_000;

export type InvalidateKeys = readonly QueryKey[];
type KeysFor = InvalidateKeys | ((variables: unknown, data: unknown) => InvalidateKeys);

export type AppMutationMeta = {
  // Everything the write can change, refreshed after it.
  invalidates?: KeysFor;
  // What the screen after the save reads, which the save waits for. Named, the
  // rest of `invalidates` refreshes in the background; left out, the save waits
  // for all of it. A cached query is refetched even when nothing on screen
  // reads it, since the next screen has not mounted yet.
  awaits?: KeysFor;
  // false when the write cannot change anything the status payload carries.
  // "await" when the caller reads the status as soon as the save settles (it
  // routes on it, or reseeds a form from it), so the save waits for it.
  status?: boolean | "await";
};

declare module "@tanstack/vue-query" {
  interface Register {
    mutationMeta: AppMutationMeta;
  }
}

export function shouldRetry(failureCount: number, error: unknown): boolean {
  if (failureCount >= MAX_RETRIES) return false;
  if (!isApiError(error) || error.handled) return false;
  if (error.kind === "network") return true;
  return error.kind === "http" && error.status >= 500 && error.status !== 504 && error.code !== "err.databaseReadOnly";
}

export function retryDelay(attempt: number, error: unknown): number {
  const after = isApiError(error) ? error.retryAfter : null;
  if (after != null) return Math.min(after * 1000, MAX_RETRY_DELAY_MS);
  return Math.min(1000 * 2 ** attempt, MAX_RETRY_DELAY_MS);
}

export interface AppQueryClientOptions {
  // The portal's status query, invalidated by every successful mutation.
  statusKey?: QueryKey;
  queries?: DefaultOptions["queries"];
  mutations?: DefaultOptions["mutations"];
}

// For code outside a component. Components use useQueryClient().
let appClient: QueryClient | undefined;

export function setAppQueryClient(client: QueryClient | undefined): void {
  appClient = client;
}

export function appQueryClient(): QueryClient {
  if (!appClient) throw new Error("The app's query client is not installed yet.");
  return appClient;
}

// The screen after a save has not mounted yet, so what it reads is often not
// active, and refetchQueries skips a query whose only observers are disabled:
// KnocForm reads the Knocs list for an nginx knoc only, so on every other
// knoc's Settings tab a save waited for nothing. The queries are fetched
// directly instead, joining one already in flight (cancelRefetch: false).
function refetchForNextScreen(client: QueryClient, queryKey: QueryKey): Promise<unknown> {
  void client.invalidateQueries({ queryKey, refetchType: "none" });
  const queries = client
    .getQueryCache()
    .findAll({ queryKey })
    .filter((query) => query.options.queryFn && query.options.queryFn !== skipToken);
  return Promise.all(queries.map((query) => query.fetch(undefined, { cancelRefetch: false }).catch(() => undefined)));
}

// Resolves when the work does or the time is up, whichever comes first.
function settleWithin(work: Promise<unknown>, ms: number): Promise<void> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const cap = new Promise<void>((resolve) => (timer = setTimeout(resolve, ms)));
  return Promise.race([work.then(() => undefined), cap]).finally(() => clearTimeout(timer));
}

export function createAppQueryClient(options: AppQueryClientOptions = {}): QueryClient {
  // Assigned below: the cache must exist before the client that owns it.
  let client: QueryClient | undefined;

  const mutationCache = new MutationCache({
    onSuccess: (data, variables, _onMutateResult, mutation) => {
      if (!client) return;
      const meta = mutation.options.meta;
      const keys = (spec: KeysFor | undefined) => (typeof spec === "function" ? spec(variables, data) : (spec ?? []));
      const named = keys(meta?.invalidates);
      const target = client;
      let waited: Promise<unknown>[];
      if (meta?.awaits === undefined) {
        waited = named.map((queryKey) => target.invalidateQueries({ queryKey }));
      } else {
        const awaited = keys(meta.awaits);
        // The background pass leaves the awaited queries to the fetch below: a
        // refetch under a broader key (knocs, around knocs/list) would cancel it,
        // and the save would stop waiting.
        const background = (query: { queryKey: QueryKey }) =>
          !awaited.some((queryKey) => partialMatchKey(query.queryKey, queryKey));
        for (const queryKey of named) void target.invalidateQueries({ queryKey, predicate: background });
        waited = awaited.map((queryKey) => refetchForNextScreen(target, queryKey));
      }
      if (options.statusKey && meta?.status !== false) {
        const status = target.invalidateQueries({ queryKey: options.statusKey });
        if (meta?.status === "await") waited.push(status);
      }
      if (!waited.length) return;
      return settleWithin(Promise.all(waited), MUTATION_REFETCH_WAIT_MS);
    },
  });

  client = new QueryClient({
    mutationCache,
    defaultOptions: {
      queries: {
        staleTime: DEFAULT_STALE_TIME_MS,
        gcTime: DEFAULT_GC_TIME_MS,
        networkMode: "always",
        // On, it refetches every stale query on screen, dashboard aggregates
        // included, whenever the tab regains focus. A screen that needs it opts in.
        refetchOnWindowFocus: false,
        refetchOnReconnect: true,
        retry: shouldRetry,
        retryDelay,
        ...options.queries,
      },
      mutations: {
        networkMode: "always",
        retry: false,
        ...options.mutations,
      },
    },
  });
  return client;
}
